import { addDays, endOfDay, format, startOfDay, subDays, subMonths, subYears } from 'date-fns';

import type { DashboardFilters } from './dashboard.wire';

// Filtros do Dashboard como o legado monta (pages/Dashboard/**). Puro: a tela guarda o estado e o agente usa
// as mesmas funções.

/** Os status da primeira consulta (Dashboard/index.tsx:20). */
// eslint-disable-next-line lingui/no-unlocalized-strings -- valores do enum de status do fio
export const DEFAULT_STATUS_LIST = ['ComplianceApproval', 'CreditApproval', 'Signatures', 'SignaturesValidation'] as const;

/** Os botões do período, na ordem do legado (FilterByPeriod.tsx:13). */
export const PERIOD_PRESETS = ['today', 'yesterday', '1m', '3m', '6m', '1y'] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

const iso = (date: Date) => date.toISOString();

/** Primeira consulta: hoje, de 00:00:00.000 a 23:59:59.999 (Dashboard/index.tsx:17-22). */
export function initialDashboardFilters(now: Date = new Date()): DashboardFilters {
  return {
    initialDate: iso(startOfDay(now)),
    finalDate: iso(endOfDay(now)),
    statusList: [...DEFAULT_STATUS_LIST],
  };
}

/**
 * Período pelo botão (FilterByPeriod.tsx:18-47): Hoje e Ontem cobrem o dia inteiro; 1M, 3M, 6M e 1 ANO
 * começam na mesma hora de agora, no passado, e terminam **daqui a 30 dias**. Mantém produtos e status.
 */
export function presetRange(preset: PeriodPreset, now: Date = new Date()): Pick<DashboardFilters, 'initialDate' | 'finalDate'> {
  const ahead = iso(addDays(now, 30));
  switch (preset) {
    case 'today':
      return { initialDate: iso(startOfDay(now)), finalDate: iso(endOfDay(now)) };
    case 'yesterday': {
      const day = subDays(now, 1);
      return { initialDate: iso(startOfDay(day)), finalDate: iso(endOfDay(day)) };
    }
    case '1m':
      return { initialDate: iso(subMonths(now, 1)), finalDate: ahead };
    case '3m':
      return { initialDate: iso(subMonths(now, 3)), finalDate: ahead };
    case '6m':
      return { initialDate: iso(subMonths(now, 6)), finalDate: ahead };
    case '1y':
      return { initialDate: iso(subYears(now, 1)), finalDate: ahead };
  }
}

const civil = /^(\d{4})-(\d{2})-(\d{2})$/;

function localDay(value: string): Date {
  const match = civil.exec(value);
  if (!match) throw new Error(`invalid civil date: ${value}`);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** "Definir período" (HeaderInformationBox.tsx:45-58): `yyyy-MM-dd'T'00:00:00XX` a `yyyy-MM-dd'T'23:59:59XX`, no fuso local. */
export function customRange(start: string, end: string): Pick<DashboardFilters, 'initialDate' | 'finalDate'> {
  return {
    initialDate: format(localDay(start), "yyyy-MM-dd'T'00:00:00XX"),
    finalDate: format(localDay(end), "yyyy-MM-dd'T'23:59:59XX"),
  };
}

/** Período novo sobre os filtros atuais: produtos e status ficam (FilterByPeriod.tsx:39-46). */
export function withPeriod(filters: DashboardFilters, range: Pick<DashboardFilters, 'initialDate' | 'finalDate'>): DashboardFilters {
  return {
    initialDate: range.initialDate,
    finalDate: range.finalDate,
    ...(filters.statusList ? { statusList: filters.statusList } : {}),
    ...(filters.productList ? { productList: filters.productList } : {}),
  };
}

/**
 * "Confirmar" na gaveta de produtos (SelectProductContainer.tsx:46-61), fiel ao legado: a lista de status
 * **sai** da consulta, e uma segunda seleção junta a nova com a anterior (repetindo ids).
 */
export function withProducts(filters: DashboardFilters, selected: readonly string[], previous: readonly string[] | null): DashboardFilters {
  const merge = previous !== null && previous.length > 0 && selected.length > 0;
  return {
    initialDate: filters.initialDate,
    finalDate: filters.finalDate,
    productList: merge ? [...selected, ...previous] : [...selected],
  };
}

/** Hoje − 7 dias até hoje: o intervalo que o calendário do legado abre (Calendar.tsx:57-61), em datas civis. */
export function defaultCustomRange(now: Date = new Date()): { readonly start: string; readonly end: string } {
  return { start: format(subDays(now, 7), 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
}
